import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import {
  TOOL_IDS,
  useCurrentProject,
  useCurrentProjectId,
} from '@adsk/alloy-provider';
import { callOrReturn } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';

import ReferencePickerBlockGroup from './blocks/ReferencePickerBlockGroup';
import getPluginKey from './helpers/getPluginKey';
import { isPluginProductOrToolNotAvailableInEntitlements } from './helpers/isPluginProductOrToolNotAvailableInEntitlements';
import {
  pluginShape,
  relationshipShape,
  entityCacheShape,
  createFormPropsShape,
} from './helpers/propTypes';
import useRemoveEntity from './hooks/useRemoveEntity';
import ReferencePickerEmptyState from './ReferencePickerEmptyState';
import ReferencePickerError from './ReferencePickerError';
import ReferencePickerLoader from './ReferencePickerLoader';
import ReferencePickerModalContainer from './ReferencePickerModalContainer';
import ReferencePickerTitle from './ReferencePickerTitle';
import ReferencePickerTopBar from './ReferencePickerTopBar';
import ReferencePickerTypeButton from './ReferencePickerTypeButton';
import RemoveConfirmationModal from './RemoveConfirmationModal';

/**
 * A ReferencePickerView
 * docs.
 */
const ReferencePickerView = React.forwardRef((props, ref) => {
  const {
    className,
    style,
    sourceEntityId,
    sourceEntityDomain,
    sourceEntityType,
    container,
    targetPlugins,
    allowAddingLinks,
    allowRemoveLinkedEntity,
    onRemove,
    onAddRelationships,
    title,
    addButtonText,
    emptyStateText,
    createFormProps,
    dataViewProps,
    relationshipsData,
    entitiesCache,
    addEntitiesToCache,
    updateEntitiesInCache,
    onAddReferenceClick,
    onReferenceClickAnalytics,
    shouldHideReferencesThatAreNotAvailableInEntitlements,
    loadingShouldHideReferencesThatAreNotAvailableInEntitlements,
  } = props;
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const projectId = useCurrentProjectId();
  const currentProject = useCurrentProject();
  const [pickerModalPlugin, setPickerModalPlugin] = useState(null);
  const openPickerModal = (plugin) => {
    setPickerModalPlugin(plugin);
    onAddReferenceClick && onAddReferenceClick(plugin.metadata);
  };
  const onClosePickerModal = () => {
    setPickerModalPlugin(null);
  };
  const listTitle =
    'title' in props ? title : T.translate('REFERENCE_PICKER_TITLE');
  const shouldRenderPickerModal = allowAddingLinks || pickerModalPlugin;

  const {
    loading: loadingRelationships,
    error: errorLoadingRelationships,
    retry: reloadRelationships,
    data: allRelationships,
  } = relationshipsData;

  const pickerModalRelationships = useMemo(
    () =>
      (pickerModalPlugin && allRelationships
        ? allRelationships.filter(
            ({ domain, type }) =>
              domain === pickerModalPlugin.metadata.domain &&
              type === pickerModalPlugin.metadata.type,
          )
        : []) || [],
    [pickerModalPlugin, allRelationships],
  );

  const pickerModalRelationshipData = useMemo(
    () => ({
      data: pickerModalRelationships,
      loading: loadingRelationships,
      error: errorLoadingRelationships,
      retry: reloadRelationships,
    }),
    [
      errorLoadingRelationships,
      loadingRelationships,
      pickerModalRelationships,
      reloadRelationships,
    ],
  );

  const {
    dataToConfirmRemoving,
    setDataToConfirmRemoving,
    isRemoving,
    closeConfirmRemoveModal,
    handleRemoveConfirm,
  } = useRemoveEntity(onRemove);

  const pluginsSupportContent = targetPlugins.filter(
    (plugin) => !plugin.isPickerOnly,
  );

  const groupData = pluginsSupportContent.map((plugin) => ({
    plugin,
    entityCache:
      entitiesCache?.[plugin.metadata.domain]?.[plugin.metadata.type],
    relationships: allRelationships
      ? allRelationships.filter(
          ({ domain, type }) =>
            domain === plugin.metadata.domain && type === plugin.metadata.type,
        )
      : [],
  }));

  const isBlocksEmpty =
    !groupData.length || groupData.every((g) => !g.relationships.length);

  // using memo here for better readability, having a short condition here is very confusing
  const showMainLoader = useMemo(() => {
    if (loadingShouldHideReferencesThatAreNotAvailableInEntitlements) {
      return true;
    }

    if (loadingRelationships && isBlocksEmpty) {
      return true;
    }

    if (!projectId) {
      return true;
    }

    return false;
  }, [
    loadingShouldHideReferencesThatAreNotAvailableInEntitlements,
    loadingRelationships,
    isBlocksEmpty,
    projectId,
  ]);

  const showEmptyState =
    !showMainLoader && !errorLoadingRelationships && isBlocksEmpty;
  const showContent =
    !showMainLoader && !errorLoadingRelationships && !isBlocksEmpty;

  const pluginsAllowingSearch = targetPlugins.filter(
    (plugin) => plugin.queryEntities,
  );

  const addText = addButtonText || T.translate('ADD_REFERENCES');

  return (
    <div
      ref={ref}
      className={className}
      css={[
        {
          border: `1px solid ${theme.colors.charcoal200}`,
          borderRadius: 2,
        },
        style,
      ]}
    >
      {(listTitle || allowAddingLinks) && (
        <ReferencePickerTopBar>
          {listTitle && (
            <ReferencePickerTitle>{listTitle}</ReferencePickerTitle>
          )}
          {allowAddingLinks && (
            <ReferencePickerTypeButton
              loadingShouldHideReferencesThatAreNotAvailableInEntitlements={
                loadingShouldHideReferencesThatAreNotAvailableInEntitlements
              }
              openPickerModal={openPickerModal}
              plugins={pluginsAllowingSearch}
              shouldHideReferencesThatAreNotAvailableInEntitlements={
                shouldHideReferencesThatAreNotAvailableInEntitlements
              }
            >
              {addText}
            </ReferencePickerTypeButton>
          )}
        </ReferencePickerTopBar>
      )}

      <div>
        {showContent && (
          <div>
            {groupData.map(({ plugin, entityCache, relationships }) => {
              const shouldHideBlock =
                currentProject &&
                shouldHideReferencesThatAreNotAvailableInEntitlements &&
                isPluginProductOrToolNotAvailableInEntitlements({
                  plugin,
                  currentProject,
                  bypassToolIds: [TOOL_IDS.PHOTOS],
                });

              if (shouldHideBlock) {
                return null;
              }

              return (
                <ReferencePickerBlockGroup
                  key={getPluginKey(plugin)}
                  plugin={plugin}
                  relationships={relationships}
                  entitiesCache={entityCache}
                  allowRemoveLinkedEntity={allowRemoveLinkedEntity}
                  setDataToConfirmRemoving={setDataToConfirmRemoving}
                  updateEntitiesInCache={updateEntitiesInCache}
                  onReferenceClickAnalytics={onReferenceClickAnalytics}
                  sourceEntityId={sourceEntityId}
                  sourceEntityDomain={sourceEntityDomain}
                  sourceEntityType={sourceEntityType}
                  shouldHideReferencesThatAreNotAvailableInEntitlements={
                    shouldHideReferencesThatAreNotAvailableInEntitlements
                  }
                />
              );
            })}
          </div>
        )}
        {showMainLoader && <ReferencePickerLoader />}
        {errorLoadingRelationships && (
          <ReferencePickerError onTryAgain={reloadRelationships} />
        )}
        {showEmptyState && (
          <ReferencePickerEmptyState
            emptyStateText={emptyStateText}
            plugins={pluginsAllowingSearch}
            allowAddingLinks={allowAddingLinks}
          />
        )}
      </div>
      {shouldRenderPickerModal && (
        <ReferencePickerModalContainer
          sourceEntityId={sourceEntityId}
          sourceEntityDomain={sourceEntityDomain}
          sourceEntityType={sourceEntityType}
          plugin={pickerModalPlugin}
          open={!!pickerModalPlugin}
          closeModal={onClosePickerModal}
          onAddRelationships={onAddRelationships}
          relationshipsData={pickerModalRelationshipData}
          addEntitiesToCache={addEntitiesToCache}
          updateEntitiesInCache={updateEntitiesInCache}
          container={container}
          dataViewProps={
            dataViewProps && pickerModalPlugin
              ? callOrReturn(dataViewProps, {
                  domain: pickerModalPlugin.metadata.domain,
                  type: pickerModalPlugin.metadata.type,
                })
              : undefined
          }
          createFormProps={
            createFormProps && pickerModalPlugin
              ? callOrReturn(createFormProps, {
                  domain: pickerModalPlugin.metadata.domain,
                  type: pickerModalPlugin.metadata.type,
                })
              : undefined
          }
        />
      )}
      {showContent && (
        <RemoveConfirmationModal
          plugin={dataToConfirmRemoving?.plugin}
          entity={dataToConfirmRemoving?.entity}
          container={container}
          closeConfirmRemoveModal={closeConfirmRemoveModal}
          onRemove={handleRemoveConfirm}
          isRemoving={isRemoving}
        />
      )}
    </div>
  );
});

ReferencePickerView.displayName = 'ReferencePickerView';

ReferencePickerView.propTypes = {
  /**
   *  Styles applied to the root element
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * Class applied to the root element
   */
  className: PropTypes.string,
  /**
   * The entity ID we want to link to
   */
  sourceEntityId: PropTypes.string.isRequired,
  /**
   * The source entity domain, as used by relationship service.
   */
  sourceEntityDomain: PropTypes.string.isRequired,
  /**
   * The source entity type, as used by relationship service.
   */
  sourceEntityType: PropTypes.string.isRequired,
  /**
   * The plugins we want to allow linking to
   */
  targetPlugins: PropTypes.arrayOf(pluginShape).isRequired,
  /**
   * A container to attach popups to
   */
  container: PropTypes.object,
  /**
   * Whether or not the user can add linked entities
   */
  allowAddingLinks: PropTypes.bool,
  /**
   * Whether or not remove link is allowed for linked entities. Can be bool for all or func to set it for each entity.
   * If function, shape is: ({ relationship, entity }) => *
   */
  allowRemoveLinkedEntity: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  /**
   * Handler to remove entity relationship. Should return a promise for delete relationship request.
   * Shape: (relationship) => *
   */
  onRemove: PropTypes.func.isRequired,
  /**
   * Handler to add entity relationships. Should return a promise for add relationship request, resolving to the newly created relationships.
   * Receives two argument:
   * 1. The source entity to link all entities to, of shape: {domain, id, type};
   * 2. An array of entities to link to, of shape: [{domain, id, type}, ...]
   *
   * The relationship service accepts at most 20 entities to link in one request. To prevent an error
   * from occuring when adding > 20 entities, feature teams should either do batching between the FE and BE,
   * or between the BE and the relationship service. We don't add batching here because the Reference Picker
   * is not responsible for network requests, it's only expected to invoke the onAddRelationships received via props
   */
  onAddRelationships: PropTypes.func.isRequired,
  /**
   * Title to show instead of the default one.
   */
  title: PropTypes.string,
  /**
   * Add button text to show instead of the default one.
   */
  addButtonText: PropTypes.string,
  /**
   * Empty state text to show instead of the default one.
   */
  emptyStateText: PropTypes.string,
  /**
   * Create form props by type. Either an object that would be passed to create form, or a function if need to return different props per entity type.
   * For example:
   * createFormProps={({ domain, type }) => ({ custom props })}
   */
  createFormProps: PropTypes.oneOfType([createFormPropsShape, PropTypes.func]),
  /**
   * Additional props to pass to DataView. Either an object that would be passed to DataView, or a function if need to return props as per entity type.
   * For example:
   * dataViewProps={({ domain, type}) => ({ custom props })}
   */
  dataViewProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * The relationship data.
   */
  relationshipsData: PropTypes.shape({
    /** The parsed relationship data relevant for reference picker  */
    data: PropTypes.arrayOf(relationshipShape),
    /** Whether we currently load relationships */
    loading: PropTypes.bool,
    /** Whether fetching relationship data raised an error */
    error: PropTypes.bool,
    /** Retry loading relationships in case of an error. Required when `errorLoadingRelationships` is true */
    retry: PropTypes.func,
  }).isRequired,
  /**
   * A cache of all entities by domain, type & ID.
   */
  entitiesCache: entityCacheShape.isRequired,
  /**
   * A handle method to add entities to cache.
   * The handler is of shape { entities, domain, type } => *
   */
  addEntitiesToCache: PropTypes.func,
  /**
   * A handle method to update entities in the cache.
   * The handler is of shape { entities, domain, type } => *
   */
  updateEntitiesInCache: PropTypes.func,
  /**
   * Callback fired when user opens picker modal with a reference type.
   * Handler of shape: ({ type, domain }) => *
   */
  onAddReferenceClick: PropTypes.func,
  /**
   * Callback fired when user clicks a specific reference. This callback doesn't block the native click support.
   * The general use case is for handling analytics on reference clicks.
   * Handler of shape: ({ type, domain, id }) => *
   */
  onReferenceClickAnalytics: PropTypes.func,
  /**
   * Whether or not to hide references that are not available in entitlements
   */
  shouldHideReferencesThatAreNotAvailableInEntitlements: PropTypes.bool,
  /**
   * Whether to force loading state
   */
  loadingShouldHideReferencesThatAreNotAvailableInEntitlements: PropTypes.bool,
};

export default ReferencePickerView;
